import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'

import styles from './Field.module.css'

interface Common {
  label: string
  hint?: ReactNode
  error?: string
}

export type FieldProps = Common &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & { multiline?: false }

export type TextAreaProps = Common &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & { multiline: true }

/**
 * Поле ввода с лейблом, подсказкой и ошибкой.
 *
 * Лейбл обязателен и не заменяется плейсхолдером: плейсхолдер исчезает,
 * когда человек начал печатать, и через два поля уже непонятно, что где.
 *
 * Ошибка объявлена через aria-describedby, а не только цветом рамки —
 * то же правило, что и с danger-кнопкой: один цвет никогда не единственный
 * носитель смысла.
 */
export function Field({ label, hint, error, multiline, className, ...rest }: FieldProps | TextAreaProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errId = `${id}-err`

  const described = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined

  const controlClass = [styles.control, error ? styles.invalid : null].filter(Boolean).join(' ')

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>

      {multiline ? (
        <textarea
          id={id}
          className={controlClass}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          id={id}
          className={controlClass}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          {...(rest as InputHTMLAttributes<HTMLInputElement>)}
        />
      )}

      {hint && !error ? (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      ) : null}

      {error ? (
        <span id={errId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}
